<div class="bw-panel" [class.bw-panel--phone]="isSheet" data-test-id="birth-window-panel">
  <header class="bw-panel__header">
    <div class="bw-panel__heading">
      <h3 class="bw-panel__title">{{ labelsVm().title }}</h3>
      @if (state.chartName()) {
        <span class="bw-panel__chart">{{ state.chartName() }}</span>
      }
    </div>
    <div class="bw-panel__meta">
      <span class="bw-panel__born">{{ bornText() }}</span>
      <span class="bw-panel__clock">{{ birthClockText() }}</span>
    </div>
    <e-segmented-toggle
      class="bw-panel__window"
      size="xs"
      [options]="windowOptions()"
      [value]="windowValue()"
      [groupLabel]="labelsVm().windowGroup"
      testId="birth-window-width"
      (valueChange)="setWindow($event)" />
  </header>

  @if (!available()) {
    <p class="bw-panel__unavailable">{{ labelsVm().unavailable }}</p>
  } @else {
    @if (isSheet) {
      <!-- Phone: de marge-kaartjes zijn keuzetegels, één laag tegelijk (Steven 2026-10-17). -->
      <div class="bw-tiles" role="tablist">
        @for (tile of phoneTiles(); track tile.tier) {
          <button type="button" role="tab"
                  [class]="'bw-tier bw-tile bw-tier--' + tile.color"
                  [class.bw-tile--active]="phoneTier() === tile.tier"
                  [class.bw-tile--muted]="tile.muted"
                  [style.--bw-tier-accent]="tile.accent"
                  [attr.aria-selected]="phoneTier() === tile.tier"
                  [attr.aria-label]="tileSelectLabel(tile)"
                  [attr.data-test-id]="'birth-window-tile-' + tile.tier"
                  (click)="selectPhoneTier(tile.tier)">
            <span class="bw-tier__head">
              <span class="bw-tier__dot" aria-hidden="true"></span>
              <span class="bw-tier__label">{{ tile.label }}</span>
              @if (tile.critical) {
                <i class="fas fa-exclamation-circle bw-tier__critical" aria-hidden="true"></i>
              }
              <i class="bw-tile__check fas" [class.fa-circle-check]="phoneTier() === tile.tier" [class.fa-circle]="phoneTier() !== tile.tier" aria-hidden="true"></i>
            </span>
            @if (tile.colorLabel) {
              <span class="bw-tier__color">{{ tile.colorLabel }}</span>
            }
            <span class="bw-tier__text">{{ tile.text }}</span>
          </button>
        }
      </div>
    } @else {
      <div class="bw-panel__tiers">
        @for (card of tierCards(); track card.tier) {
          <div class="bw-tier" [class]="'bw-tier bw-tier--' + card.color" [style.--bw-tier-accent]="card.accent" [attr.title]="card.hint">
            <div class="bw-tier__head">
              <span class="bw-tier__dot" aria-hidden="true"></span>
              <span class="bw-tier__label">{{ card.label }}</span>
              <span class="bw-tier__color">{{ card.colorLabel }}</span>
              @if (card.critical) {
                <i class="fas fa-exclamation-circle bw-tier__critical" [attr.title]="labelsVm().critical" aria-hidden="true"></i>
              }
            </div>
            <div class="bw-tier__text">{{ card.text }}</div>
            @if (card.drivers) {
              <div class="bw-tier__drivers">{{ card.drivers }}</div>
            }
          </div>
        }
      </div>
    }

    @if (state.loading() && lanes().length === 0) {
      <div class="bw-panel__status" role="status" aria-live="polite">
        <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i> {{ loadingText() }}
      </div>
    } @else if (state.error() !== undefined && lanes().length === 0) {
      <div class="bw-panel__status bw-panel__status--error" role="alert">
        <span>{{ errorText() }}</span>
        <button eBtn size="xs" color="elegant" type="button" (click)="retry()">{{ labelsVm().retry }}</button>
      </div>
    } @else if (groups().length > 0 && isSheet) {
      <!-- Phone: de chart achter het oog (standaard dicht), scrolt gewoon mee; de lanes dragen zelf hun labels. -->
      <div class="bw-phone-chart">
        <div class="bw-panel__chart-head">
          <span class="bw-panel__chart-caption" [class.bw-panel__chart-caption--candidate]="state.candidateMs() !== undefined">
            {{ chartCaption() }}
            @if (chartExactPending()) {
              <i class="fas fa-circle-notch fa-spin bw-panel__chart-pending" [attr.title]="labelsVm().chartExactPending" aria-hidden="true"></i>
            }
          </span>
          <button type="button" class="bw-panel__chart-toggle"
                  [attr.aria-pressed]="phoneChartVisible()"
                  [attr.title]="phoneChartVisible() ? labelsVm().chartHide : labelsVm().chartShow"
                  data-test-id="birth-window-chart-toggle"
                  (click)="togglePhoneChart()">
            <i class="fas" [class.fa-eye-slash]="phoneChartVisible()" [class.fa-eye]="!phoneChartVisible()" aria-hidden="true"></i>
          </button>
        </div>
        @if (phoneChartVisible()) {
          <div class="bw-panel__chart-host" data-test-id="birth-window-chart">
            <hd-bodygraph-chart
              [configuration]="chartConfiguration()"
              [transitOverlay]="chartTransitOverlay()"
              interactionMode="event-only"
              [contextMenuEnabled]="false" />
          </div>
        }
      </div>

      <p class="bw-panel__hint">{{ hintText() }}</p>

      <hd-property-lanes-vertical
        class="bw-panel__lanes bw-panel__lanes--vertical"
        [class.bw-panel__lanes--loading]="state.loading()"
        [groups]="phoneGroups()"
        [fromMs]="state.data()!.fromMs"
        [toMs]="state.data()!.toMs"
        [anchorMs]="state.data()!.birthMs"
        [anchorLabel]="anchorLabel()"
        [markerMs]="state.candidateMs()"
        [markerLabel]="markerLabel()"
        [markerDraggable]="canInteract()"
        [markerHandleLabel]="labelsVm().markerHandle"
        [timeZone]="state.timeZoneId()"
        [lang]="lang()"
        [ariaLabel]="labelsVm().ariaLanes"
        (blockClicked)="onBlockClicked($event)"
        (markerChanged)="onMarkerChanged($event)"
        (markerCommitted)="onMarkerCommitted($event)"
        (emptyClicked)="onEmptyClicked($event)" />

      <ng-container *ngTemplateOutlet="legend" />
    } @else if (groups().length > 0) {
      <p class="bw-panel__hint">{{ hintText() }}</p>

      <div class="bw-panel__body" [class.bw-panel__body--chart-hidden]="!state.chartVisible()">
        <!-- De chart op de kandidaattijd staat in het paneel zelf (feedbackronde 1): zo zie je
             de wissels ook met een losgemaakt paneel, zonder dat de hoofdchart mee verandert. -->
        <aside class="bw-panel__chart-column">
          <div class="bw-panel__chart-head">
            <span class="bw-panel__chart-caption" [class.bw-panel__chart-caption--candidate]="state.candidateMs() !== undefined">
              {{ chartCaption() }}
              @if (chartExactPending()) {
                <i class="fas fa-circle-notch fa-spin bw-panel__chart-pending" [attr.title]="labelsVm().chartExactPending" aria-hidden="true"></i>
              }
            </span>
            <button type="button" class="bw-panel__chart-toggle"
                    [attr.aria-pressed]="state.chartVisible()"
                    [attr.title]="state.chartVisible() ? labelsVm().chartHide : labelsVm().chartShow"
                    data-test-id="birth-window-chart-toggle"
                    (click)="toggleChart()">
              <i class="fas" [class.fa-eye-slash]="state.chartVisible()" [class.fa-eye]="!state.chartVisible()" aria-hidden="true"></i>
            </button>
          </div>
          @if (state.chartVisible()) {
            <div class="bw-panel__chart-host" data-test-id="birth-window-chart">
              <hd-bodygraph-chart
                [configuration]="chartConfiguration()"
                [transitOverlay]="chartTransitOverlay()"
                interactionMode="event-only"
                [contextMenuEnabled]="false" />
            </div>
          }
        </aside>

        <div class="bw-panel__lanes-column">
          <hd-property-lanes
            class="bw-panel__lanes"
            [class.bw-panel__lanes--loading]="state.loading()"
            [groups]="groups()"
            [fromMs]="state.data()!.fromMs"
            [toMs]="state.data()!.toMs"
            [anchorMs]="state.data()!.birthMs"
            [anchorLabel]="anchorLabel()"
            [markerMs]="state.candidateMs()"
            [markerLabel]="markerLabel()"
            [markerDraggable]="canInteract()"
            [timeZone]="state.timeZoneId()"
            [lang]="lang()"
            [ariaLabel]="labelsVm().ariaLanes"
            (blockClicked)="onBlockClicked($event)"
            (markerChanged)="onMarkerChanged($event)"
            (markerCommitted)="onMarkerCommitted($event)"
            (emptyClicked)="onEmptyClicked($event)"
            (groupToggled)="onGroupToggled($event)" />

          <ng-container *ngTemplateOutlet="legend" />
        </div>
      </div>
    }

    @if (canInteract()) {
      <footer class="bw-panel__commit" [class.bw-panel__commit--active]="state.candidateMs() !== undefined">
        <div class="bw-panel__candidate">
          <span class="bw-panel__candidate-label">{{ labelsVm().candidate }}</span>
          <span class="bw-panel__candidate-value">{{ candidateText() || anchorLabel() }}</span>
        </div>
        <div class="bw-panel__steps">
          <button eBtn size="xs" squared color="elegant" icon="backward-step" type="button" [disabled]="!state.data()" (click)="jumpToChange(-1)"></button>
          <button eBtn size="xs" squared color="elegant" icon="minus" type="button" [disabled]="!state.data()" (click)="stepCandidate(-60000)"></button>
          <button eBtn size="xs" squared color="elegant" icon="plus" type="button" [disabled]="!state.data()" (click)="stepCandidate(60000)"></button>
          <button eBtn size="xs" squared color="elegant" icon="forward-step" type="button" [disabled]="!state.data()" (click)="jumpToChange(1)"></button>
        </div>
        <div class="bw-panel__actions">
          <button eBtn size="sm" color="elegant" icon="rotate-left" type="button"
                  [disabled]="state.candidateMs() === undefined || state.committing()"
                  (click)="resetCandidate()">{{ labelsVm().reset }}</button>
          <button eBtn size="sm" color="primary-75" icon="check" type="button"
                  [disabled]="state.candidateMs() === undefined || state.committing()"
                  [attr.aria-busy]="state.committing()"
                  data-test-id="birth-window-apply"
                  (click)="commit()">{{ labelsVm().apply }}</button>
        </div>
      </footer>
    }
  }
</div>

<!-- Legende in één regel, eigen stijl (bolletjes + korte woorden), geen Base-regel:
     die toelichting staat in de groepskop van Base. -->
<ng-template #legend>
  <p class="bw-panel__legend" data-test-id="birth-window-legend">
    <span class="bw-legend bw-legend--birth">{{ labelsVm().legendBirth }}</span>
    <span class="bw-legend bw-legend--same">{{ labelsVm().legendSame }}</span>
    <span class="bw-legend bw-legend--different">{{ labelsVm().legendDifferent }}</span>
    <span class="bw-legend bw-legend--candidate">{{ labelsVm().legendCandidate }}</span>
  </p>
</ng-template>
